import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Content, Link, Page } from '@backstage/core-components';
import Grid from '@material-ui/core/Grid';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import { fade, makeStyles } from '@material-ui/core/styles';
// import LogoFull from '../Root/LogoFull';
import LogoFull from '../../logos/idp-logo-full.png';
import {
  VISIT_HISTORY_UPDATED_EVENT,
  formatRelativeTime,
  getRecentlyVisited,
  getTopVisited,
  readVisitHistory,
  type VisitRecord,
} from './visitHistory';

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: '100%',
    background:
      'radial-gradient(circle at top, rgba(125, 243, 225, 0.08), transparent 30%), linear-gradient(180deg, #2c2c2c 0%, #242424 100%)',
  },
  content: {
    maxWidth: '100%',
    // margin: '0 auto',
    padding: theme.spacing(8, 2, 6),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(5, 2, 4),
    },
  },
  searchForm: {
    width: '100%',
    maxWidth: 1100,
    margin: `0 auto ${theme.spacing(4)}px`,
  },
  hero: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(5),
  },
  logoWrap: {
    '& svg': {
      height: '92px !important',
      maxWidth: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      '& svg': {
        height: '52px !important',
      },
    },
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 74,
    padding: theme.spacing(0, 2.5),
    borderRadius: 37,
    backgroundColor: fade('#5d5d5d', 0.42),
    boxShadow: `0 10px 28px ${fade('#000', 0.22)}`,
    border: `1px solid ${fade('#fff', 0.08)}`,
    width: '100%',
  },
  searchIcon: {
    color: fade('#fff', 0.45),
    marginRight: theme.spacing(2),
    fontSize: 32,
  },
  searchInput: {
    flex: 1,
    color: theme.palette.common.white,
    fontSize: 20,
    '& input::placeholder': {
      color: fade('#fff', 0.58),
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  clearButton: {
    border: 0,
    background: 'transparent',
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    padding: theme.spacing(1),
    textTransform: 'uppercase',
  },
  panel: {
    minHeight: 380,
    backgroundColor: fade('#5a5a5a', 0.36),
    color: theme.palette.common.white,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${fade('#fff', 0.08)}`,
    boxShadow: `0 8px 24px ${fade('#000', 0.18)}`,
  },
  panelHeader: {
    padding: theme.spacing(3.25, 3.5, 2.5),
    borderBottom: `1px solid ${fade('#fff', 0.08)}`,
  },
  panelTitle: {
    fontSize: 28,
    fontWeight: 700,
  },
  panelBody: {
    padding: theme.spacing(1.5, 3.5, 3),
  },
  visitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    minHeight: 56,
  },
  badge: {
    flex: '0 0 auto',
    padding: theme.spacing(0.6, 1.3),
    borderRadius: 999,
    backgroundColor: '#121212',
    color: theme.palette.common.white,
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 700,
    textTransform: 'lowercase',
  },
  visitLink: {
    color: '#89c6ff',
    fontSize: 18,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  visitMeta: {
    color: fade('#fff', 0.72),
    fontSize: 16,
  },
  emptyState: {
    color: fade('#fff', 0.72),
    fontSize: 16,
    lineHeight: 1.6,
    marginTop: theme.spacing(2),
  },
  panelsGrid: {
    width: '100%',
    maxWidth: 1180,
    margin: '0 auto',
  },
}));

const VisitPanel = ({
  title,
  visits,
  meta,
}: {
  title: string;
  visits: VisitRecord[];
  meta: (visit: VisitRecord) => string;
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.panel} elevation={0}>
      <div className={classes.panelHeader}>
        <Typography component="h2" className={classes.panelTitle}>
          {title}
        </Typography>
      </div>
      <div className={classes.panelBody}>
        {visits.length > 0 ? (
          visits.map(visit => (
            <div key={visit.path} className={classes.visitRow}>
              <span className={classes.badge}>other</span>
              <Link
                to={visit.path}
                className={classes.visitLink}
                underline="none"
              >
                {visit.title}
              </Link>
              <span className={classes.visitMeta}>{meta(visit)}</span>
            </div>
          ))
        ) : (
          <Typography className={classes.emptyState}>
            Visit a few pages and your activity will show up here.
          </Typography>
        )}
      </div>
    </Paper>
  );
};

export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [visits, setVisits] = useState<VisitRecord[]>(() => readVisitHistory());

  useEffect(() => {
    const handleVisitUpdate = () => setVisits(readVisitHistory());

    window.addEventListener(VISIT_HISTORY_UPDATED_EVENT, handleVisitUpdate);

    return () => {
      window.removeEventListener(
        VISIT_HISTORY_UPDATED_EVENT,
        handleVisitUpdate,
      );
    };
  }, []);

  const topVisited = useMemo(() => getTopVisited(visits, 5), [visits]);
  const recentlyVisited = useMemo(
    () => getRecentlyVisited(visits, 5),
    [visits],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = query.trim();
    navigate(
      trimmedQuery
        ? `/search?query=${encodeURIComponent(trimmedQuery)}`
        : '/search',
    );
  };

  return (
    <Page themeId="home" className={classes.page}>
      <Content className={classes.content}>
        <div className={classes.hero}>
          <div className={classes.logoWrap}>
            <img src={LogoFull} alt="idp-logo" style={{ width: '32rem' }} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className={classes.searchForm}>
          <Paper component="div" className={classes.searchBar} elevation={0}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              placeholder="Search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              inputProps={{ 'aria-label': 'Search the portal' }}
            />
            <button
              type="button"
              className={classes.clearButton}
              onClick={() => setQuery('')}
            >
              Clear
            </button>
          </Paper>
        </form>

        <Grid container spacing={3} className={classes.panelsGrid}>
          <Grid item xs={12} md={6}>
            <VisitPanel
              title="Top Visited"
              visits={topVisited}
              meta={visit =>
                `${visit.count} ${visit.count === 1 ? 'time' : 'times'}`
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <VisitPanel
              title="Recently Visited"
              visits={recentlyVisited}
              meta={visit => formatRelativeTime(visit.lastVisitedAt)}
            />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
